/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {SelectionModel} from "@angular/cdk/collections";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from "@angular/core";
import {MatTableDataSource} from "@angular/material/table";
import {ConfigSectionComponent} from "@app/models/components/config-section.component";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {CommonState} from "@app/store/common/common.state";
import {Microlearning} from "@doctor-panel-modules/patient-treatment-plan/models/microlearning";
import {PatientTreatmentPlanAdvState} from "@doctor-panel-modules/patient-treatment-plan/store/patient-treatment-plan-adv.state";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {UserEntry2} from "@trec/api/registry";
import {StoreAction} from "@trec/components/store";
import {BehaviorSubject, combineLatest, Observable, Subscription} from "rxjs";
import {TpAdvActions} from "../../../store/patient-treatment-plan-adv.actions";

@Component({
  selector: "c3-config-microlearning",
  templateUrl: "./config-microlearning.component.html",
  styleUrls: ["./config-microlearning.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigMicrolearningComponent
  extends ConfigSectionComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Select(PatientTreatmentPlanAdvState.availableMicrolearning)
  availableMicrolearning$: Observable<Microlearning[] | undefined>;

  @Select(CommonState.patientUserEntry)
  patientUserEntry$: Observable<UserEntry2[] | null | undefined>;

  @Select(hasActionsExecuting([TpAdvActions.FetchMicrolearning, TpAdvActions.UpdateMicrolearning]))
  loading$: Observable<boolean>;

  @Input()
  context: RegistryContexts[];

  private valid = new BehaviorSubject(false);
  private touched = new BehaviorSubject(false);

  get touched$(): Observable<boolean> {
    return this.touched.asObservable();
  }

  get valid$(): Observable<boolean> {
    return this.valid.asObservable();
  }

  /**
   * List of columns to display in the table.
   */
  displayedColumns: string[] = ["select", "name", "startDate"];

  // Private fields ///////////////////////////////////////////////////////////////////////////////

  dataSource = new MatTableDataSource<Microlearning>();

  // Selection //////////////////////////////////
  readonly initialSelection: Microlearning[] = [];
  selection = new SelectionModel<Microlearning>(true, this.initialSelection);

  readonly today = new Date();

  private subscr: Subscription;

  constructor(private store: Store, private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    // Fetch available microlearnings
    this.store.dispatch(new TpAdvActions.FetchMicrolearning());
  }

  ngAfterViewInit() {
    this.subscr = combineLatest([this.availableMicrolearning$, this.patientUserEntry$]).subscribe(
      ([availableList, ue]) => {
        if (availableList && ue) {
          const ueKeysMicroLearning = ue.filter(x => x.getKeys("MICROLEARNING"));

          let list: Microlearning[] = [];
          let newSelection: Microlearning[] = [];
          ueKeysMicroLearning.forEach(ueKeys => {
            const mlKeys = ueKeys.getKeys("MICROLEARNING");

            // Copy available pes (objects from store are freezed)
            list = [...availableList
              .map((g) => ({...g}))
              .map((g) => {
                // Find ml in keys
                const mlKey = mlKeys.find((key) => key.name === g.id);
                return mlKey?.start_date ? {...g, startDate: new Date(mlKey.start_date)} : g;
              })];

            // Select current active ml
            const selection = list.filter((g) => {
              const mlKey = mlKeys.find((key) => key.name === g.id);
              return mlKey;
            });
            // Select current active goals
            newSelection = newSelection?.concat(selection);
          });

          this.dataSource.data = list;
          this.selection = new SelectionModel(true, newSelection);

          this.valid.next(true);
          this.cd.markForCheck();
        }
      }
    );
  }

  /**
   * Callback called when user select/deselact a feature.
   */
  onSelectionChange(ml: Microlearning) {
    this.touched.next(true);
    this.selection.toggle(ml);

    this.updateValidity();
  }

  /**
   * Whether the number of selected elements matches the total number of rows.
   */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /**
   * Selects all rows if they are not all selected; otherwise clear selection.
   */
  masterToggle() {
    this.touched.next(true);

    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));

    this.updateValidity();
  }

  onStartDateChange() {
    this.touched.next(true);
    this.updateValidity();
  }

  prepareSaveDataAction(): StoreAction | null {
    // TODO validate

    // Create the selection array
    const mls: Microlearning[] = this.selection.selected;
    // Trigger update
    return new TpAdvActions.UpdateMicrolearning({selectedMls: mls});
  }

  private updateValidity() {
    // Find a microlearning without startDate -> if exists, something is invalid
    const elemWithoutStarteDate = this.selection.selected.find((ml) => !ml.startDate);

    if (elemWithoutStarteDate) {
      this.valid.next(false);
    } else {
      this.valid.next(true);
    }
  }

  resetTouched(): void {
    this.touched.next(false);
  }

  ngOnDestroy() {
    if (this.subscr) {
      this.subscr.unsubscribe();
    }

    this.valid.complete();
    this.touched.complete();
  }
}
